import { Badge, Flex, Link, Text } from '@radix-ui/themes'
import { ChevronDownIcon, ChevronUpIcon } from '@radix-ui/react-icons'
import { useCallback, useLayoutEffect, useRef, useState } from 'react'

type CollapsibleTagsProps = {
  tags: string[]
  variant?: 'text' | 'pills'
}

const COLLAPSED_HEIGHT_PX = 28

export const CollapsibleTags = ({ tags, variant = 'text' }: CollapsibleTagsProps) => {
  const [expanded, setExpanded] = useState(false)
  const [scrollHeight, setScrollHeight] = useState(0)
  const contentRef = useRef<HTMLDivElement>(null)

  const toggle = useCallback(() => setExpanded((prev) => !prev), [])

  useLayoutEffect(() => {
    if (contentRef.current) {
      setScrollHeight(contentRef.current.scrollHeight)
    }
  }, [tags])

  if (tags.length === 0) {
    return null
  }

  if (variant === 'pills') {
    const isOverflowing = scrollHeight > COLLAPSED_HEIGHT_PX
    const needsToggle = isOverflowing || expanded

    return (
      <Flex direction="column" gap="2">
        <div
          ref={contentRef}
          style={{
            maxHeight: expanded ? scrollHeight : COLLAPSED_HEIGHT_PX,
            overflow: 'hidden',
            transition: 'max-height 0.25s ease',
          }}>
          <Flex gap="3" wrap="wrap" align="center">
            {tags.map((t) => (
              <Badge key={t} variant="outline" radius="full">
                {t}
              </Badge>
            ))}
          </Flex>
        </div>
        {needsToggle && (
          <Link size="1" onClick={toggle} style={{ cursor: 'pointer' }}>
            <Flex align="center" gap="1">
              {expanded ? (
                <>
                  Show less <ChevronUpIcon />
                </>
              ) : (
                <>
                  Show all ({tags.length}) <ChevronDownIcon />
                </>
              )}
            </Flex>
          </Link>
        )}
      </Flex>
    )
  }

  return (
    <Flex direction="column" gap="2">
      <Flex gap="2" align="center">
        <Text weight="bold">Tags:</Text>
        <Link size="2" onClick={toggle} style={{ cursor: 'pointer' }}>
          <Flex align="center" gap="1">
            {expanded ? (
              <>
                Hide ({tags.length}) <ChevronUpIcon />
              </>
            ) : (
              <>
                Show ({tags.length}) <ChevronDownIcon />
              </>
            )}
          </Flex>
        </Link>
      </Flex>
      <div
        ref={contentRef}
        style={{
          maxHeight: expanded ? scrollHeight : 0,
          overflow: 'hidden',
          transition: 'max-height 0.25s ease',
        }}>
        <Text>{tags.join(', ')}</Text>
      </div>
    </Flex>
  )
}
